import axios, { AxiosError } from 'axios';
import type { AxiosInstance } from 'axios';
import { debug } from '../utils/debug';
import type {
  User,
  OnboardingStatus,
  OnboardingStatusResponse,
  OnboardingStepData,
  UserPreferences,
  ApiResponse,
  ApiError,
  AuthVerifyResponse,
  SupportTicket,
  SupportTicketMessage,
  CreateTicketRequest,
  SendMessageRequest,
  Friend,
  FriendshipRequest,
  UserSearchResult,
  SendFriendRequestRequest,
} from '../types';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: '/api', // Use relative path - will go through Vite proxy
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

    // Add request interceptor to include Telegram init data
    this.client.interceptors.request.use((config) => {
      // Debug logging for Telegram WebApp data
      debug.log('🔍 FRONTEND DEBUG - Telegram WebApp availability:', {
        windowTelegram: !!window.Telegram,
        webApp: !!window.Telegram?.WebApp,
        initData: window.Telegram?.WebApp?.initData,
        initDataUnsafe: window.Telegram?.WebApp?.initDataUnsafe,
        user: window.Telegram?.WebApp?.initDataUnsafe?.user,
        version: window.Telegram?.WebApp?.version,
        platform: window.Telegram?.WebApp?.platform,
      });

      // Шлём ОБА способа авторизации независимо: email-токен (Bearer) и/или Telegram initData.
      // Бэкенд сначала пробует Bearer как api_token, если не сошлось — валидирует initData.
      // Иначе завалявшийся в localStorage email-токен перекрывал initData и ломал вход через Telegram.
      const emailToken = localStorage.getItem('email_auth_token');
      if (emailToken) {
        config.headers['Authorization'] = `Bearer ${emailToken}`;
      }
      if (window.Telegram?.WebApp?.initData) {
        config.headers['X-Telegram-Init-Data'] = window.Telegram.WebApp.initData;
      }
      if (!emailToken && !window.Telegram?.WebApp?.initData) {
        debug.warn('⚠️ FRONTEND DEBUG - No auth credentials available!');
      }
      
      return config;
    });

    // Add response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError<ApiError>) => {
        debug.error('❌ FRONTEND DEBUG - API Error:', {
          status: error.response?.status,
          data: error.response?.data,
          headers: error.response?.headers,
          url: error.config?.url,
        });
        return Promise.reject(error);
      }
    );
  }

  // Health check
  async health(): Promise<{ status: string; timestamp: string }> {
    const response = await this.client.get('/health');
    return response.data;
  }

  // Auth endpoints
  async verifyAuth(): Promise<AuthVerifyResponse> {
    try {
      const response = await this.client.get<AuthVerifyResponse>('/auth/verify');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // Handle 401 specifically
        if (error.response?.status === 401) {
          return {
            success: false,
            authenticated: false,
            error: error.response?.data?.error || 'Authentication failed',
          };
        }
        throw error;
      }
      throw error;
    }
  }

  // User endpoints
  async getUserProfile(): Promise<User> {
    const response = await this.client.get<ApiResponse<User>>('/user/profile');
    return response.data.data;
  }

  // Onboarding Status endpoints
  async getOnboardingStatus(): Promise<OnboardingStatus> {
    const response = await this.client.get<OnboardingStatusResponse>('/user/onboarding-status');
    return response.data.data;
  }

  async completeOnboarding(skipped: boolean = false, data?: OnboardingStepData): Promise<OnboardingStatus> {
    const response = await this.client.post<OnboardingStatusResponse>('/user/complete-onboarding', {
      skipped,
      ...data
    });
    return response.data.data;
  }

  // Referral endpoints
  async getReferralInfo(): Promise<{ referral_info: any }> {
    const response = await this.client.get<{ success: boolean; referral_info: any }>('/user/referral-info');
    return response.data;
  }

  async getReferrals(page: number = 1, perPage: number = 10): Promise<any> {
    const response = await this.client.get(`/user/referrals?page=${page}&per_page=${perPage}`);
    return response.data;
  }

  // Tasks endpoints
  async getTasks(): Promise<any> {
    const response = await this.client.get('/tasks');
    return response.data;
  }

  async completeTask(id: string): Promise<any> {
    const response = await this.client.post(`/tasks/${id}/complete`);
    return response.data;
  }

  async updateTaskProgress(id: string, increment: number = 1): Promise<any> {
    const response = await this.client.post(`/tasks/${id}/progress`, { increment });
    return response.data;
  }

  // Journal endpoints
  async getJournalEntries(): Promise<any> {
    const response = await this.client.get('/journal');
    return response.data;
  }

  async createJournalEntry(entry: { date: string; mood: string; note: string; activities?: string[] }): Promise<any> {
    const response = await this.client.post('/journal', entry);
    return response.data;
  }

  async updateJournalEntry(id: string, entry: Partial<{ date: string; mood: string; note: string; activities?: string[] }>): Promise<any> {
    const response = await this.client.put(`/journal/${id}`, entry);
    return response.data;
  }

  async deleteJournalEntry(id: string): Promise<any> {
    const response = await this.client.delete(`/journal/${id}`);
    return response.data;
  }

  // Stats endpoint
  async getStats(): Promise<any> {
    const response = await this.client.get('/user/stats');
    return response.data;
  }

  // Transactions endpoint
  async getTransactions(): Promise<any> {
    const response = await this.client.get('/user/transactions');
    return response.data;
  }

  // Meditation endpoints
  async getMeditations(category?: string, difficulty?: string): Promise<any> {
    let url = '/meditations';
    const params = new URLSearchParams();
    if (category && category !== 'all') params.append('category', category);
    if (difficulty && difficulty !== 'all') params.append('difficulty', difficulty);
    
    if (params.toString()) {
      url += '?' + params.toString();
    }
    
    const response = await this.client.get(url);
    return response.data;
  }

  async getMeditation(id: string): Promise<any> {
    const response = await this.client.get(`/meditations/${id}`);
    return response.data;
  }

  async startMeditation(id: string): Promise<any> {
    const response = await this.client.post(`/meditations/${id}/start`);
    return response.data;
  }

  async completeMeditation(id: string, userLocalHour?: number): Promise<any> {
    const response = await this.client.post(`/meditations/${id}/complete`, {
      user_local_hour: userLocalHour
    });
    return response.data;
  }

  async getPracticeDays(year: number, month: number): Promise<any> {
    const response = await this.client.get('/user/practice-days', {
      params: { year, month }
    });
    return response.data;
  }

  // Preferences endpoints
  async getPreferences(): Promise<UserPreferences> {
    const response = await this.client.get<ApiResponse<UserPreferences>>('/user/preferences');
    return response.data.data;
  }

  async updatePreferences(preferences: Partial<UserPreferences>): Promise<UserPreferences> {
    const response = await this.client.put<ApiResponse<UserPreferences>>('/user/preferences', preferences);
    return response.data.data;
  }

  // Support endpoints
  async getSupportTickets(): Promise<SupportTicket[]> {
    const response = await this.client.get<{ success: boolean; data: SupportTicket[] }>('/support/tickets');
    return response.data.data;
  }

  async getSupportTicket(id: number): Promise<SupportTicket> {
    const response = await this.client.get<{ success: boolean; data: SupportTicket }>(`/support/tickets/${id}`);
    return response.data.data;
  }

  async createSupportTicket(data: CreateTicketRequest): Promise<SupportTicket> {
    const response = await this.client.post<{ success: boolean; data: SupportTicket }>('/support/tickets', data);
    return response.data.data;
  }

  async sendSupportMessage(ticketId: number, data: SendMessageRequest): Promise<SupportTicketMessage> {
    const response = await this.client.post<{ success: boolean; data: SupportTicketMessage }>(`/support/tickets/${ticketId}/messages`, data);
    return response.data.data;
  }

  // Friendship endpoints
  async getFriends(): Promise<Friend[]> {
    const response = await this.client.get<{ success: boolean; data: Friend[] }>('/friendship/friends');
    return response.data.data;
  }

  async getIncomingFriendRequests(): Promise<FriendshipRequest[]> {
    const response = await this.client.get<{ success: boolean; data: FriendshipRequest[] }>('/friendship/requests/incoming');
    return response.data.data;
  }

  async getOutgoingFriendRequests(): Promise<FriendshipRequest[]> {
    const response = await this.client.get<{ success: boolean; data: FriendshipRequest[] }>('/friendship/requests/outgoing');
    return response.data.data;
  }

  async sendFriendRequest(data: SendFriendRequestRequest): Promise<{ id: number; status: string }> {
    const response = await this.client.post<{ success: boolean; message: string; data: { id: number; status: string } }>('/friendship/request', data);
    return response.data.data;
  }

  async acceptFriendRequest(requestId: number): Promise<{ id: number; status: string }> {
    const response = await this.client.post<{ success: boolean; message: string; data: { id: number; status: string } }>(`/friendship/request/${requestId}/accept`);
    return response.data.data;
  }

  async rejectFriendRequest(requestId: number): Promise<{ id: number; status: string }> {
    const response = await this.client.post<{ success: boolean; message: string; data: { id: number; status: string } }>(`/friendship/request/${requestId}/reject`);
    return response.data.data;
  }

  async searchUsers(query: string): Promise<UserSearchResult[]> {
    const response = await this.client.get<{ success: boolean; data: UserSearchResult[] }>(`/friendship/search?q=${encodeURIComponent(query)}`);
    return response.data.data;
  }

  // Push notification settings endpoints
  async getPushSettings(): Promise<Record<string, { name: string; description: string | null; enabled: boolean }>> {
    const response = await this.client.get<{ success: boolean; data: Record<string, { name: string; description: string | null; enabled: boolean }> }>('/pushes/settings');
    return response.data.data;
  }

  async updatePushSettings(settings: Record<string, boolean>): Promise<Record<string, { name: string; description: string | null; enabled: boolean }>> {
    const response = await this.client.put<{ success: boolean; data: Record<string, { name: string; description: string | null; enabled: boolean }> }>('/pushes/settings', { settings });
    return response.data.data;
  }

  async togglePushSetting(type: string): Promise<{ type: string; enabled: boolean }> {
    const response = await this.client.post<{ success: boolean; data: { type: string; enabled: boolean } }>(`/pushes/settings/${type}/toggle`);
    return response.data.data;
  }

  // Language settings
  async updateLanguage(languageCode: string): Promise<{ success: boolean; language_code: string }> {
    const response = await this.client.put<{ success: boolean; language_code: string }>('/user/language', { language_code: languageCode });
    return response.data;
  }

  // PaymentsSBP endpoints
  async createPaymentSBP(amount: number): Promise<{
    success: boolean;
    payment_url?: string;
    payment_id?: string;
    amount?: number;
    price_rub?: number;
    message?: string;
  }> {
    const response = await this.client.post<{
      success: boolean;
      payment_url?: string;
      payment_id?: string;
      amount?: number;
      price_rub?: number;
      message?: string;
    }>('/payments-sbp/create', { amount });
    return response.data;
  }

  async getPaymentLimits(): Promise<{
    success: boolean;
    data: {
      min_amount: number;
      max_amount: number;
      crystals_per_rub: number;
    };
  }> {
    const response = await this.client.get<{
      success: boolean;
      data: {
        min_amount: number;
        max_amount: number;
        crystals_per_rub: number;
      };
    }>('/payments-sbp/limits');
    return response.data;
  }

  async emailRegister(data: { email: string; password: string; first_name: string }): Promise<{ success: boolean; token: string; user: User }> {
    const response = await this.client.post('/auth/email/register', data);
    return response.data;
  }

  async emailLogin(data: { email: string; password: string }): Promise<{ success: boolean; token: string; user: User }> {
    const response = await this.client.post('/auth/email/login', data);
    return response.data;
  }

  async emailLogout(): Promise<void> {
    await this.client.post('/auth/email/logout');
  }

  // WhatsApp: запросить код на номер
  async whatsappRequestCode(data: { phone: string }): Promise<{ success: boolean }> {
    const response = await this.client.post('/auth/whatsapp/request-code', data);
    return response.data;
  }

  // WhatsApp: подтвердить код -> вход
  async whatsappVerifyCode(data: { phone: string; code: string }): Promise<{ success: boolean; token: string; user: User }> {
    const response = await this.client.post('/auth/whatsapp/verify-code', data);
    return response.data;
  }
}

// Create singleton instance
export const apiClient = new ApiClient();

// Utility function to handle API errors
export const handleApiError = (error: AxiosError<ApiError>): string => {
  if (error.response?.data?.error) {
    return error.response.data.error;
  }
  
  if (error.response?.status === 401) {
    return 'Authentication failed. Please restart the app.';
  }
  
  if (error.response?.status === 429) {
    return 'Too many requests. Please slow down.';
  }
  
  if (error.response && error.response.status >= 500) {
    return 'Server error. Please try again later.';
  }
  
  return error.message || 'An unexpected error occurred';
};
